import { cx } from '@emotion/css';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import UseOutsideClick from '../../../helpers/hooks/UseOutsideClick';
import { SizeVariants, Theme } from '../../../theme/themes';
import { makeCSS } from '../../../utils/makeCSS';
import { ContextMenu } from '../../molecules/ContextMenu';
import {
  InputFieldTempate,
  InputFieldTempateProps
} from '../InputField/InputFieldTempate';
import { Placement } from '@floating-ui/dom';
import { Button } from '../Button';
import { isMobile } from 'react-device-detect';

// eslint-disable-next-line @typescript-eslint/no-empty-interface

/**
 *The Select component allows users pick a value from a predefined list of options.
 Each option item inside the Select component needs to be wrap with the Option component.
 */
export interface SelectProps
  extends React.PropsWithChildren,
    Pick<InputFieldTempateProps, 'width'> {
  /**Label of the options or first option to be displayed */
  placeholder?: string;
  /** selected value */
  value: string;
  disabled?: boolean;
  onChange?(value: string): void;
  /** A prop that specifies how to render the selected value. */
  renderValue?: (props) => JSX.Element;
  anchorPlacement?: Placement;
  criticalState?: boolean;
  /** Whether should hide the selected value in the input field
   * - use case: only show `placeholder` in the input
   */
  hideSelectedValue?: boolean;
  /**Define a different width for the select menu*/
  dropDownWidth?: number | string;
  showClearButton?: boolean;
  autoAdjustPlacement?: boolean;
}

const useCSS = makeCSS(({ theme, width }: SelectProps & { theme: Theme }) => {
  return {
    root: {
      cursor: 'pointer',
      position: 'relative',
      display: 'flex',
      width: width === '100%' ? '100%' : 'auto',
      gap: '8px',
      '& > div': {
        width: 'inherit'
      }
    },
    contextMenu: {
      display: 'none',
      alignContent: 'start',
      position: 'absolute',
      zIndex: 999
    },
    placeholder: {
      pointerEvents: 'none'
    },
    extraPadding: {
      paddingTop: theme.spacing[3],
      paddingBottom: theme.spacing[3]
    }
  };
});

const Select = (props: SelectProps) => {
  const {
    placeholder = 'Select option...',
    children,
    value,
    width = '100%',
    disabled,
    onChange,
    renderValue,
    anchorPlacement = 'bottom-start',
    criticalState = false,
    hideSelectedValue = false,
    dropDownWidth,
    showClearButton,
    autoAdjustPlacement = false,
    ...restProps
  } = props;

  const classes = useCSS({ width });

  const state = false;

  const [icon, setIcon] = useState<'unfold_more' | 'unfold_less'>(
    'unfold_more'
  );
  const [opened, setOpened] = useState(state || false);
  const [selectedOption, setSelectedOption] = useState<React.ReactNode>(
    React.Children.toArray(children).find(
      (c) => (c as ReactElement).props.value === value
    ) || null
  );

  useEffect(() => {
    setOpened(state || false);
  }, [state]);

  const handleOnOpen = useCallback(() => {
    setOpened(!opened);
    setIcon(opened ? 'unfold_more' : 'unfold_less');
  }, [opened, setOpened]);

  React.useLayoutEffect(() => {
    if ((selectedOption as ReactElement)?.props.value === value) {
      return;
    }

    React.Children.forEach(children, (child) => {
      if ((child as ReactElement).props.value === value) {
        setSelectedOption(child || null);
      }
    });
  }, [value]);

  useLayoutEffect(() => {
    if ((selectedOption as ReactElement)?.props.value === value) {
      return;
    }

    onChange?.((selectedOption as ReactElement)?.props.value);
  }, [selectedOption]);

  //close options menu when clicked anywhere else
  const handleClickOutside = useCallback(() => {
    setOpened(false);
    setIcon('unfold_more');
  }, [setOpened]);

  const options = useMemo(() => {
    return React.Children.map(children, (child) => {
      return React.cloneElement(child as React.ReactElement, {
        onSelect: () => setSelectedOption(child as React.ReactElement)
      });
    });
  }, [children, setSelectedOption]);

  const ref = UseOutsideClick(handleClickOutside);

  const onRightIconClick = useCallback(
    (event) => {
      event.stopPropagation();
      handleOnOpen();
    },
    [handleOnOpen]
  );

  const onClear = useCallback(() => {
    setSelectedOption(null);
    onChange?.(null);
  }, [onChange]);

  const anchorRect = ref.current?.getBoundingClientRect();
  const offset = anchorRect?.height / 2 + 8; // 8 = the gap between the input and the pop over menu
  const toBottom = window.innerHeight - (anchorRect?.bottom || 0) - offset;
  const toTop = anchorRect ? anchorRect.top - anchorRect.height - offset : 0;
  const selectedValue = renderValue ? renderValue({ value }) : selectedOption;
  const placement = !autoAdjustPlacement
    ? anchorPlacement
    : ((toBottom >= toTop / 2
        ? anchorPlacement.replace('top', 'bottom')
        : anchorPlacement.replace('bottom', 'top')) as Placement);

  return (
    <div ref={ref} className={`${classes.root}`} role="listbox">
      <div onClick={!disabled ? handleOnOpen : undefined}>
        <InputFieldTempate
          {...restProps}
          width={width}
          label={selectedValue ? '' : placeholder}
          size={SizeVariants.md}
          iconRight={icon}
          criticalState={criticalState}
          disabled={disabled}
          onRightIconClick={onRightIconClick}
        >
          {!hideSelectedValue && selectedValue && (
            <div
              className={cx(
                classes.placeholder,
                selectedOption === null ? classes.extraPadding : ''
              )}
            >
              {selectedValue}
            </div>
          )}
        </InputFieldTempate>
      </div>
      <select className={`${classes.contextMenu}`} onClick={handleOnOpen}>
        {opened && (
          <ContextMenu
            width={
              dropDownWidth ??
              anchorRect?.width -
                (showClearButton && selectedValue ? 75 : 0) +
                'px'
            }
            placement={placement}
            anchor={ref.current}
            scrollbarHeight={
              placement.includes('bottom')
                ? toBottom
                : placement.includes('top')
                ? toTop
                : undefined
            }
          >
            {options}
          </ContextMenu>
        )}
      </select>
      {showClearButton && selectedValue && (
        <div
          style={{
            marginLeft: isMobile ? '' : 8,
            marginTop: isMobile ? '-5px' : ''
          }}
        >
          <Button
            iconLeft={isMobile ? 'delete' : undefined}
            label={isMobile ? '' : 'Clear'}
            onClick={onClear}
            size={isMobile ? SizeVariants.lg : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default Select;
export { Select };
